import { anzeigeNummer, sortiereKandidaten } from '../../domain/candidateSort';
import { offeneAufgaben } from '../../domain/scoring';
import type { Candidate, CandidateState } from '../../domain/types';
import { usePoints, useT } from '../../i18n';
import {
  requireCandidate,
  selectExam,
  selectStation,
  selectTasks,
  useAppStore,
  type CandidateFilter,
} from '../../store/useAppStore';
import { Icon, type IconName } from '../../ui/Icon';
import { Sheet, SheetAction } from '../../ui/Sheet';
import { SortierWahl } from '../../ui/SortierWahl';
import styles from './CandidatesSheet.module.css';

const FILTERS: CandidateFilter[] = ['Alle', 'Offen', 'Bewertet'];

const STATE_ICON: Record<CandidateState, IconName> = {
  bewertet: 'check',
  laeuft: 'clock',
  wartet: 'hourglass',
  entschuldigt: 'minus',
};

/** Prüflingsliste mit Suche und Filter; die Wahl geht über eine Rückfrage. */
export function CandidatesSheet() {
  const t = useT();
  const exam = useAppStore(selectExam);
  const station = useAppStore(selectStation);
  const current = useAppStore(requireCandidate);
  const closeSheet = useAppStore((s) => s.closeSheet);
  const openSheet = useAppStore((s) => s.openSheet);
  const filter = useAppStore((s) => s.candidateFilter);
  const setFilter = useAppStore((s) => s.setCandidateFilter);
  const search = useAppStore((s) => s.candidateSearch);
  const setSearch = useAppStore((s) => s.setCandidateSearch);
  const sortierung = useAppStore((s) => s.candidateSort);
  const setSortierung = useAppStore((s) => s.setCandidateSort);
  const askSwitch = useAppStore((s) => s.askSwitch);
  const submitted = useAppStore((s) => s.submitted);
  const tasks = useAppStore(selectTasks);
  const assessments = useAppStore((s) => s.assessments);

  /**
   * Wie viele Aufgaben bei einem Prüfling an **dieser** Station noch offen sind.
   *
   * ⚠️ **Nur dort, wo wir es wirklich wissen.** Die Zahl steht an einem Prüfling, für den auf
   * diesem Gerät ein angefangener Bogen liegt. Für einen, der noch gar nicht dran war, wäre
   * „alle offen" zwar rechnerisch richtig, aber als Ansage falsch — er wartet ja noch. Und ein
   * abgesendeter Bogen zeigt seine Punktzahl, nicht seinen Rest.
   *
   * Marktübliche Apps führen dafür eine Spalte (`docs/oberflaeche-luecken.md`, Punkt 2); ohne
   * sie muss der Prüfer jeden Bogen einzeln öffnen, um zu sehen, ob noch etwas fehlt.
   */
  const offenVon = (id: string): number | null => {
    if (submitted[id]) return null;
    const stand = assessments[id];
    if (!stand) return null;
    return offeneAufgaben(tasks, stand);
  };

  /**
   * Was auf diesem Gerät abgesendet wurde, schlägt den Stand aus der Prüfungsverwaltung —
   * sonst stünde ein gerade bewerteter Prüfling weiter als „läuft" in der Liste.
   */
  const withLocal = exam.candidates.map((c) => {
    const record = submitted[c.id];
    return record ? { ...c, state: 'bewertet' as const, score: record.total } : c;
  });

  const rated = withLocal.filter((c) => c.state === 'bewertet').length;
  const needle = search.trim().toLowerCase();

  const visible = withLocal.filter((c) => {
    const byFilter =
      filter === 'Alle' ||
      (filter === 'Bewertet' && c.state === 'bewertet') ||
      (filter === 'Offen' && c.state !== 'bewertet');
    // ⚠️ Gesucht wird über das, was **gedruckt** vorliegt: Name, Matrikelnummer,
    // Prüfungsnummer. Die technische Kennung stand hier früher mit drin — im Classic-Modus
    // ist das `classic:candidate:200`, und danach tippt niemand.
    const byNeedle =
      !needle ||
      c.name.toLowerCase().includes(needle) ||
      c.matriculationNumber.toLowerCase().includes(needle) ||
      (c.examNo ?? '').toLowerCase().includes(needle);
    return byFilter && byNeedle;
  });

  const geordnet = sortiereKandidaten(visible, sortierung);

  return (
    <Sheet
      kicker={t.candidates.kicker + ' · ' + station.label}
      kickerIcon="user"
      title={t.candidates.summary(withLocal.length, rated)}
      onClose={closeSheet}
      actions={
        <>
          <SheetAction
            icon="qr"
            label={t.candidates.scan}
            onClick={() => openSheet({ kind: 'scanner' })}
            variant="outline"
          />
          <SheetAction icon="close" label={t.candidates.close} onClick={closeSheet} iconOnly />
        </>
      }
    >
      <div className={styles.filterRow}>
        <input
          className={styles.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.candidates.search}
          aria-label={t.candidates.searchLabel}
        />
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={`tap ${styles.filter}`}
            data-on={filter === f}
            onClick={() => setFilter(f)}
          >
            {f === 'Alle'
              ? t.candidates.filterAll
              : f === 'Offen'
                ? t.candidates.filterOpen
                : t.candidates.filterRated}
          </button>
        ))}
      </div>

      {/* Die Ordnung der Liste — dasselbe Bauteil wie im Startdialog, damit beide nicht
          auseinanderlaufen. Vier Wörter statt vier Zeichen: der Font unterscheidet
          „Nachname" nicht von „Vorname" (s. `ui/SortierWahl.tsx`). */}
      <div className={styles.sortHost}>
        <SortierWahl wert={sortierung} onWahl={setSortierung} />
      </div>

      <div className={styles.note}>{t.candidates.note}</div>

      <div className={`sc ${styles.list}`}>
        {geordnet.length === 0 && (
          <div className={styles.emptyList}>{t.candidates.empty}</div>
        )}
        {geordnet.map((c) => (
          <Row
            key={c.id}
            candidate={c}
            offen={offenVon(c.id)}
            on={c.id === current.id}
            onPick={() => askSwitch(c.id)}
          />
        ))}
      </div>
    </Sheet>
  );
}

function Row({
  candidate,
  offen,
  on,
  onPick,
}: {
  candidate: Candidate;
  /** Offene Aufgaben an dieser Station, oder `null`, wenn es dazu nichts zu sagen gibt. */
  offen: number | null;
  on: boolean;
  onPick: () => void;
}) {
  const t = useT();
  const points = usePoints();
  const summeZeigen = useAppStore((s) => s.bogenAnzeige.summe);
  return (
    <button type="button" className={`tap ${styles.row}`} data-on={on} onClick={onPick}>
      {/* ⚠️ **Nicht `candidate.id`.** Das ist die technische Kennung, im Classic-Modus
          `classic:candidate:200` — sie stand hier bis zum 2026-09-13 und sagte niemandem
          etwas. Gezeigt wird die Prüfungsnummer, und wo es keine gibt, die Matrikelnummer. */}
      <span className={styles.rowId} data-art={candidate.examNo ? 'pruefung' : 'matrikel'}>
        {anzeigeNummer(candidate)}
      </span>
      <span className={styles.rowName}>
        {candidate.name}
        {candidate.examNo && candidate.matriculationNumber && (
          <span className={styles.rowMatrikel}>{candidate.matriculationNumber}</span>
        )}
      </span>
      <span className={styles.state} data-state={candidate.state}>
        <Icon name={STATE_ICON[candidate.state]} size={13} />
        {/* Die Zahl **ersetzt** das Wort „läuft": sie sagt dasselbe und mehr. Bleibt nichts
            offen, steht dort, dass der Bogen vollständig ist — auch das ist eine Auskunft,
            die der Prüfer sonst nur durch Öffnen bekäme. */}
        <span>
          {offen === null
            ? t.candidates.state[candidate.state]
            : offen === 0
              ? t.candidates.komplett
              : t.candidates.offeneAufgaben(offen)}
        </span>
      </span>
      {/* `show_sum_points` aus: auch hier keine Summe — sie stünde sonst neben dem Namen. */}
      {summeZeigen && (
        <span className={styles.rowScore} data-has={candidate.score != null}>
          {candidate.score == null ? '—' : points(candidate.score)}
        </span>
      )}
    </button>
  );
}
